import React, { useEffect } from "react";
import { Script, type GetServerDataProps } from "gatsby";

import { ReactifySearchProvider, Sensors } from "@usereactify/search";

import { MetaComponent } from "@bared/ui";
import { CollectionSchema, filterProjection } from "@bared/groq";

import type { HeadProps, PageProps } from "~/types/gatsby";

import { useTripleWhale } from "~/hooks/useTripleWhale";
import { useNostoAnalytics } from "~/hooks/useNostoAnalytics";
import { useRouteLoaderData } from "~/hooks/useRouteLoaderData";

import { seoConfigForPage, getSeoMeta } from "~/lib/seo";
import { contextFromServerDataProps } from "~/lib/server";

import { ErrorPage } from "~/components/Error/Error";
import { Collection } from "~/components/Collection/Collection";

import { CustomSensor } from "~/components/CustomSensor";

import { ReviewsProvider } from "~/providers/ReviewsProvider";
import { ProductImageProvider } from "@bared/ui";

const pageQuery = filterProjection(
  CollectionSchema,
  `_type == "collection" && shopify.shopifyHandle == $handle`,
);

const COLLECTION_QUERY = /* GraphQL */ `
  #graphql
  query Collection(
    $country: CountryCode
    $handle: String!
    $language: LanguageCode
  ) @inContext(country: $country, language: $language) {
    collection(handle: $handle) {
      id
      handle
      title
      descriptionHtml
      seo {
        title
        description
      }
      image {
        openGraphUrl: url(transform: { maxWidth: 1200 })
      }
      genderMetafield: metafield(
        key: "collection_gender"
        namespace: "custom"
      ) {
        value
      }
      primaryCollection: metafield(
        key: "primary_collection"
        namespace: "custom"
      ) {
        reference {
          ... on Collection {
            id
            title
            handle
          }
        }
      }
    }
  }
` as const;

export async function getServerData(props: GetServerDataProps) {
  // Resolve context.
  const context = await contextFromServerDataProps(props);

  // Expand context.
  const { respond, notFound, groq, storefront, site, sectionResolver } =
    context;

  // Resolve handle.
  const handle = props.params?.["handle"];
  if ("string" !== typeof handle) throw new Error("No handle resolved.");

  // Fetch collection from sanity and shopify.
  const [page, shopifyResult] = await Promise.all([
    groq(pageQuery, { handle }),
    storefront.query(COLLECTION_QUERY, { variables: { handle } }),
  ]);

  // Get shopify collection from result.
  const shopifyCollection = shopifyResult.collection;

  // Collection not found, return 404.
  if (!shopifyCollection) return notFound();

  // Check if this collection is enabled for the site.
  const isEnabled =
    !page ||
    !page.sites ||
    0 === page.sites.length ||
    page.sites.includes(site.handle);

  // Collection not enabled, return 404.
  if (!isEnabled) return notFound();

  // Get the associated collection gender.
  const collectionGender =
    shopifyResult.collection && shopifyResult.collection.genderMetafield
      ? shopifyResult.collection.genderMetafield.value
      : null;

  // Resolve seo config.
  const seo = seoConfigForPage({
    context,
    sanityMetadata: page.metadata,
    media: shopifyCollection.image?.openGraphUrl,
    description: shopifyCollection.seo.description,
    title: shopifyCollection.seo.title ?? shopifyCollection.title,
  });

  // Resolve sections in the header.
  const content = page?.content
    ? await sectionResolver.resolve({ sanitySections: page.content })
    : null;

  // Resolve sections in the footer.
  const footerContent = page?.footerContent
    ? await sectionResolver.resolve({ sanitySections: page.footerContent })
    : null;

  const hideSizeFilter = !!page?.hideSizeFilter;

  // Respond with server data.
  return respond({
    seo,
    collectionGender,
    shopifyCollection,
    content,
    footerContent,
    hideSizeFilter,
  } as const);
}

export type ServerData = Awaited<ReturnType<typeof getServerData>>["props"];

export default function CollectionPage(props: PageProps<ServerData>) {
  /**
   * The key prop forces the component to remount when the URI changes rather than just passing
   * through the updated props. We have deep components which do not re-render properly when
   * their props change, so currently remounting is our only option to remain consistent.
   * @see https://bared.atlassian.net/browse/ECOM-2579
   * @see https://bared-melbourne.slack.com/archives/C045GT6BNAU/p1715903163431309
   */
  return <CollectionPageInner {...props} key={props.uri} />;
}

function CollectionPageInner({ serverData }: PageProps<ServerData>) {
  const tripleWhale = useTripleWhale();
  const nostoAnalytics = useNostoAnalytics();

  // Send tracking events when server data changes.
  useEffect(() => {
    if (200 !== serverData.status) return;

    const { shopifyCollection } = serverData;
    const category = shopifyCollection.title;

    tripleWhale.trackPageLoad("Collection");
    nostoAnalytics.trackCollectionPageView(category);
  }, [nostoAnalytics, tripleWhale, serverData]);

  const { market, enableNosto } = useRouteLoaderData("routes/$site");

  // Page found, render page.
  if (200 === serverData.status) {
    const {
      content,
      footerContent,
      hideSizeFilter,
      collectionGender,
      shopifyCollection,
    } = serverData;

    return (
      <>
        <ReviewsProvider>
          <ProductImageProvider>
            {enableNosto ? (
              <Collection
                content={content}
                footerContent={footerContent}
                title={shopifyCollection.title}
                hideSizeFilter={hideSizeFilter}
                handle={shopifyCollection.handle}
                collectionGender={collectionGender}
                descriptionHtml={shopifyCollection.descriptionHtml}
                primaryCollection={shopifyCollection.primaryCollection}
              />
            ) : (
              <ReactifySearchProvider
                mode="collection"
                includeFields={[
                  "storefrontId", // Shopify product ID for identification
                  "id", // Used by Google Analytics & Klaviyo
                  "title", // Product name/title for display
                  "handle", // URL slug for product links
                  "tags", // Product tags for badges and grouping
                  "images", // All product images (including hover)
                  "variants", // Size/color options with pricing
                  "price_min_market_*", // Minimum prices for all markets
                  "price_max_market_*", // Maximum prices for all markets
                  "inventory_quantity_market_*", // Stock quantities for all markets
                  "available_market_*", // Availability for all markets
                  "options", // Product option names (Color, Size, etc.)
                  "variant_options", // Selected option values for display

                  // // Specific metafields needed for collection page logic
                  "metafields_custom_sale_price_aud",
                  "metafields_custom_sale_price_gbp",
                  "metafields_custom_sale_price_usd",
                  "metafields_custom_sale_price_nzd",
                  "metafields_custom_sale_price_sgd",
                  "metafields_custom_sale_price_hkd",
                  "metafields_custom_netsuite_style_name", // Product style name for display
                  "metafields_custom_netsuite_class", // Gender of the product
                  "metafields_custom_netsuite_category", // Category of the product
                  "metafields_custom_netsuite_sub_category", // Sub category of the product
                  "metafields_custom_launch_date", // Launch date for new product badges
                  "metafields_custom_netsuite_status", // Product lifecycle status (Seasonal, Exit, etc.)
                  "metafields_custom_final_sale", // Boolean flag for final sale badges

                  "url", // Product URL for navigation
                ]}
                market={market.legacyId}
                additionalComponentIds={["CustomSensor"]}
                collectionHandle={shopifyCollection.handle}
                shopifyPermanentDomain="bared-footwear-au.myshopify.com"
                theme={{
                  colors: {
                    textColor: "rgb(43,43,43)",
                    primaryTextColor: "rgb(43,43,43)",
                    primaryColor: "rgb(43,43,43)",
                    titleColor: "rgb(43,43,43)",
                    alertColor: "rgb(43,43,43)",
                  },
                }}
              >
                <Sensors />
                <CustomSensor />
                <Collection
                  content={content}
                  footerContent={footerContent}
                  title={shopifyCollection.title}
                  hideSizeFilter={hideSizeFilter}
                  handle={shopifyCollection.handle}
                  collectionGender={collectionGender}
                  descriptionHtml={shopifyCollection.descriptionHtml}
                  primaryCollection={shopifyCollection.primaryCollection}
                />
              </ReactifySearchProvider>
            )}
          </ProductImageProvider>
        </ReviewsProvider>
        <Script
          strategy="idle"
          src="https://widget.reviews.io/rating-snippet/dist.js"
        />
      </>
    );
  }

  // Page not found, render error page.
  if (404 === serverData.status) {
    return <ErrorPage page={serverData.errorPage} />;
  }

  return null;
}

export function Head({ serverData }: HeadProps<ServerData>) {
  if (!serverData) return null;

  return (
    <>
      <link
        rel="stylesheet"
        href="https://widget.reviews.io/rating-snippet/dist.css"
      />
      <MetaComponent
        meta={getSeoMeta(serverData.context.rootData.seo, serverData.seo)}
      />
    </>
  );
}
